import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ResponseModel, { PaginationRes } from "../../../models/response.model";
import { IWarehouseRecord } from "../../../models/warehouse.model";
import { IWarehouseRawMaterialResponse } from "../../../models/warehouseRawMaterial.model";
import {
  cancelThirdPartyRequestApi,
  completeThirdPartyInwardApi,
  createThirdPartyInwardApi,
  createThirdPartyOutwardApi,
  getFactoriesApi,
  getStoreStocksApi,
  getThirdPartyInwardBatchesApi,
  getThirdPartyRequestsApi,
  ICreateThirdPartyInwardPayload,
  IThirdPartyOutwardPayload,
} from "../../../api/Factory/store.api";
import { isArray } from "lodash";
import {
  IThirdPartyInwardBatchesResponse,
  IThirdPartyRequest,
} from "../../../models/thirdPartyRequest.model";

interface WarehouseRawMaterials {
  warehouseId: string;
  warehouseName: string;
  warehouse: IWarehouseRecord;
  rawMaterials: IWarehouseRawMaterialResponse[];
}
interface NewStoreState {
  rawMaterialsByWarehouse: { [warehouseId: string]: WarehouseRawMaterials };
  warehouses: IWarehouseRecord[];
  thirdPartyRequests: IThirdPartyRequest[];
  thirdPartyInwardBatches: IThirdPartyInwardBatchesResponse | null;
  pagination: {
    getFloorStockPagination?: PaginationRes;
    getThirdPartyRequests?: PaginationRes;
  };
  loading: {
    getfactoriesLoading: boolean;
    getStoreStockLoading: boolean;
    thirdPartyOutwardLoading: boolean;
    thirdPartyInwardLoading: boolean;
    getThirdpartyrequestsLoading: boolean;
    cancelThirdPartyRequestLoading: boolean;
    getThirdPartyInwardBatchesLoading: boolean;
  };
}

const initialState: NewStoreState = {
  rawMaterialsByWarehouse: {},
  warehouses: [],
  thirdPartyRequests: [],
  thirdPartyInwardBatches: null,
  pagination: {},
  loading: {
    getfactoriesLoading: false,
    getStoreStockLoading: false,
    thirdPartyOutwardLoading: false,
    getThirdpartyrequestsLoading: false,
    thirdPartyInwardLoading: false,
    cancelThirdPartyRequestLoading: false,
    getThirdPartyInwardBatchesLoading: false,
  },
};

export const getStoreStock = createAsyncThunk<
  ResponseModel,
  {
    params: {
      warehouseId: string;
      page: number;
      limit: number;
      search?: string;
    };
  },
  { rejectValue: string }
>("newFloor/getStoreStock", async ({ params }, { rejectWithValue }) => {
  try {
    return await getStoreStocksApi(params);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const getFactories = createAsyncThunk<
  ResponseModel,
  {},
  { rejectValue: string }
  // eslint-disable-next-line no-empty-pattern
>("newFloor/getFactories", async ({}, { rejectWithValue }) => {
  try {
    return await getFactoriesApi();
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const createThirdPartyOutwardRequest = createAsyncThunk<
  ResponseModel,
  IThirdPartyOutwardPayload,
  { rejectValue: string }
>("thirdPartyOutward/createRequest", async (payload, { rejectWithValue }) => {
  try {
    return await createThirdPartyOutwardApi(payload);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to create outward request",
    );
  }
});

export const processThirdPartyInward = createAsyncThunk<
  ResponseModel,
  ICreateThirdPartyInwardPayload,
  { rejectValue: string }
>(
  "thirdParty/processInward",
  async (payload: ICreateThirdPartyInwardPayload, { rejectWithValue }) => {
    try {
      const response = await createThirdPartyInwardApi(payload);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to process inward entry",
      );
    }
  },
);

export const completeThirdPartyInward = createAsyncThunk<
  ResponseModel,
  {
    thirdPartyRequestId: string;
    productionCost?: number;
    receivingTransportCost?: number;
    receivingExtraCost?: number;
  },
  { rejectValue: string }
>(
  "thirdParty/completeInward",
  async (
    payload: {
      thirdPartyRequestId: string;
      productionCost?: number;
      receivingTransportCost?: number;
      receivingExtraCost?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await completeThirdPartyInwardApi(payload);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to complete inward entry",
      );
    }
  },
);

export const getThirdPartyRequests = createAsyncThunk<
  ResponseModel,
  {
    params: {
      page: number;
      limit: number;
      status?: string;
      vendorId?: string;
    };
  },
  { rejectValue: string }
>("newFloor/getThirdPartyRequests", async ({ params }, { rejectWithValue }) => {
  try {
    return await getThirdPartyRequestsApi(params);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const getThirdPartyInwardBatches = createAsyncThunk<
  ResponseModel,
  {
    params: {
      requestId: string;
    };
  },
  { rejectValue: string }
>(
  "newFloor/getThirdPartyInwardBatches",
  async ({ params }, { rejectWithValue }) => {
    try {
      return await getThirdPartyInwardBatchesApi(params);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch report",
      );
    }
  },
);

export const cancelThirdPartyRequest = createAsyncThunk<
  ResponseModel,
  { id: string },
  { rejectValue: string }
>("newFloor/cancelThirdPartyRequest", async ({ id }, { rejectWithValue }) => {
  try {
    return await cancelThirdPartyRequestApi(id);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});
const newStoreSlice = createSlice({
  name: "newstore",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStoreStock.pending, (state) => {
        state.loading.getStoreStockLoading = true;
      })
      .addCase(getStoreStock.fulfilled, (state, action) => {
        const warehouse = action.payload.data;
        if (warehouse && !isArray(warehouse)) {
          state.rawMaterialsByWarehouse[warehouse.warehouseId] = {
            ...warehouse,
            rawMaterials: warehouse.rawMaterials,
          };
          state.pagination.getFloorStockPagination = action.payload.pagination;
        }
        state.loading.getStoreStockLoading = false;
      })
      .addCase(getStoreStock.rejected, (state) => {
        state.loading.getStoreStockLoading = false;
      })
      .addCase(getFactories.pending, (state) => {
        state.loading.getfactoriesLoading = true;
      })
      .addCase(getFactories.fulfilled, (state, action) => {
        state.warehouses = action.payload.data;
        state.loading.getfactoriesLoading = false;
      })
      .addCase(getFactories.rejected, (state) => {
        state.loading.getfactoriesLoading = false;
      })
      .addCase(getThirdPartyRequests.pending, (state) => {
        state.loading.getThirdpartyrequestsLoading = true;
      })
      .addCase(getThirdPartyRequests.fulfilled, (state, action) => {
        state.thirdPartyRequests = action.payload.data;
        state.pagination.getThirdPartyRequests = action.payload.pagination;
        state.loading.getThirdpartyrequestsLoading = false;
      })
      .addCase(getThirdPartyRequests.rejected, (state) => {
        state.loading.getThirdpartyrequestsLoading = false;
      })
      .addCase(createThirdPartyOutwardRequest.pending, (state) => {
        state.loading.thirdPartyOutwardLoading = true;
      })
      .addCase(createThirdPartyOutwardRequest.fulfilled, (state) => {
        state.loading.thirdPartyOutwardLoading = false;
      })
      .addCase(createThirdPartyOutwardRequest.rejected, (state) => {
        state.loading.thirdPartyOutwardLoading = false;
      })
      .addCase(processThirdPartyInward.pending, (state) => {
        state.loading.thirdPartyInwardLoading = true;
      })
      .addCase(processThirdPartyInward.fulfilled, (state) => {
        state.loading.thirdPartyInwardLoading = false;
      })
      .addCase(processThirdPartyInward.rejected, (state) => {
        state.loading.thirdPartyInwardLoading = false;
      })
      .addCase(cancelThirdPartyRequest.pending, (state) => {
        state.loading.cancelThirdPartyRequestLoading = true;
      })
      .addCase(cancelThirdPartyRequest.fulfilled, (state) => {
        state.loading.cancelThirdPartyRequestLoading = false;
      })
      .addCase(cancelThirdPartyRequest.rejected, (state) => {
        state.loading.cancelThirdPartyRequestLoading = false;
      })
      .addCase(getThirdPartyInwardBatches.pending, (state) => {
        state.loading.getThirdPartyInwardBatchesLoading = true;
      })
      .addCase(getThirdPartyInwardBatches.fulfilled, (state, action) => {
        state.thirdPartyInwardBatches = action.payload.data;
        state.loading.getThirdPartyInwardBatchesLoading = false;
      })
      .addCase(getThirdPartyInwardBatches.rejected, (state) => {
        state.loading.getThirdPartyInwardBatchesLoading = false;
      });
  },
});

// export const {} = newStoreSlice.actions;
export default newStoreSlice.reducer;
